import React, {useState, useEffect} from 'react';
import {toast} from 'react-toastify';
import defaultStyle from './PublicProfileFeaturedContentSection.module.scss';
import desktopPreviewStyle from './PublicProfileFeaturedContentSection_desktop-preview.module.scss';
import mobilePreviewStyle from './PublicProfileFeaturedContentSection_mobile-preview.module.scss';
import {useTranslation} from 'react-i18next';
import {MediakitSectionItem} from './../../features/stores/mediakitUser.slice';
import {getCurrencyLabel} from '../../constants/currencies';
import {groupBy, numberFollowerFormat} from '../../Helper/generalHelper';
import {iconsMap, socialMediaLinks} from '../../constants/socialMedia';
import useWindowWidth from '../../hooks/useWindowWidth';
import {previewStyles} from '../../constants/theme';
import nonHookRequest from '../../API/nonHookRequest';
import videoIcon from "./../../assets/images/icons/al_player.svg";
import playIcon from './../../assets/images/icons/Play.svg';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface IPublicProfileFeaturedContentSectionItem {
  item: MediakitSectionItem;
  sectionType: String;
  theme: any;
  selectedStyle: string;
  mediakitUserId: string;
  index: number;
  ownerUserId?: string;
  nickname?: string;
  email?: string;
  setMonitizeID: React.Dispatch<React.SetStateAction<any>>;
  setMonetizeObj?: React.Dispatch<React.SetStateAction<any>>;
  activeTab: string;
}

interface IPublicProfileFeaturedContentSection extends Omit<IPublicProfileFeaturedContentSectionItem, "item" | "index" | "activeTab"> {
    items: MediakitSectionItem[];
}

const socialPlatform: any = {
    instagram: 'Instagram',
    youtube: 'Youtube',
    tiktok: 'TikTok',
};

const PublicProfileFeaturedContentSectionItem = ({
  mediakitUserId,
  sectionType,
  selectedStyle,
  item,
  index,
  theme,
  ownerUserId,
  nickname,
  email,
  setMonitizeID,
  setMonetizeObj,
  activeTab,
}: IPublicProfileFeaturedContentSectionItem) => {
  const {t, i18n} = useTranslation();
  // const fontFamily = isArabic(i18n.language) ? theme.arabicFont!: theme.englishFont!;
  const fontFamily = theme.englishFont!;
  const width = useWindowWidth();
  let styles: any = '';
  let videoCardRadius = '0px' as any;

  const shapeData: any = {}; // getShapeData(theme.cardShape!);
  if (selectedStyle === previewStyles.DESKTOP_PREVIEW) {
    styles = desktopPreviewStyle;
    videoCardRadius = shapeData?.video?.desktopPreviewBorderRadius;
  } else if (selectedStyle === previewStyles.MOBILE_PREVIEW) {
    videoCardRadius = shapeData?.video?.mobilePreviewBorderRadius;
    styles = mobilePreviewStyle;
  } else {
    styles = defaultStyle;
    videoCardRadius = shapeData?.video?.borderRadius;
  }

  const img = socialMediaLinks.filter((soc:any)=>soc.key === item.platform)[0].defaultIconTransparent;
  const videoTitle: any = item.description;

  const handleLinkCount = async () => {
    if (ownerUserId && item.id) {
      await nonHookRequest({method: 'POST', url: '/analytics/linkClick/count', body: {linkId: item.id, nickname: nickname}});
    }
  };

  const handleOpenLink = async () => {
    if (!!item?.isMonetize) {
      if (selectedStyle === previewStyles.DEFAULT) {
         setMonitizeID(item.id);
         setMonetizeObj ? setMonetizeObj({type: item.platform, title: item.contentType, note: item.description ?? '', attachment: '', amount: item?.price, url: item?.url || '', currency: item?.currency, maxPrice: (Number(1000)/100) || 1000, minPrice: (Number(5)/100) || 1}) : '';
         handleLinkCount();
     } else {
        toast(<span style={{color: 'black'}}>{'Not active from this section!'}</span>, {autoClose: 3000});
      }
      return;
    }
   
    let url = item.url;
    if (!item.url.match(/^https?:\/\//i)) {
      url = 'http://' + item.url;
    }
    window.open(url, '_blank');
    handleLinkCount();
  };

  const handleShowMoreLess = (idMore: any, idLess: any, param: any) => {
    const more: any = document.getElementById(idMore);
    const less: any = document.getElementById(idLess);
    if (more && less) {
      if (param == 'more') {
      more.style.display = 'none';
      less.style.display = 'block';
     } else {
        more.style.display = 'block';
        less.style.display = 'none';
      }
    }
  };

  const contentFooter = (
    <div className={styles.stats_container}>
        <div className={styles.stats_column}>
          <div className={styles.stats_heading}></div>
          <div className={styles.stats_data}>
            {item.channelProfileIcon && <img src={item.channelProfileIcon} className={styles.channel} />}
            {item?.platform !== 'youtube' ? <img src={iconsMap[item?.platform]} className={styles.channel} /> : null}
          </div>
        </div>
        <div className={styles.stats_column}>
          <div className={styles.stats_heading}>{t(item?.platform == 'instagram' ? 'engagements' : 'views')}</div>
          <div className={styles.stats_data}>{numberFollowerFormat(item?.metadata?.views)}</div>
        </div>
        <div className={styles.stats_column}>
          <div className={styles.stats_heading}>{t('likes')}</div>
          <div className={styles.stats_data}>{numberFollowerFormat(item?.metadata?.likes)}</div>
        </div>
        <div className={styles.stats_column}>
          <div className={styles.stats_heading}>{t('comments')}</div>
          <div className={styles.stats_data}>{numberFollowerFormat(item?.metadata?.comments)}</div>
        </div>
      </div>
  );

  const videoTitleContent = (
    videoTitle?.length > 60 ? (<>
      <div className={styles.show_less} id={`show-more-${item.id}`} onClick={(e) => {
        e.stopPropagation();
        handleShowMoreLess(`show-more-${item?.id}`, `show-less-${item.id}`, 'more');
      }}>{videoTitle?.substring(0, 60)}...</div>
      <div className={styles.show_less} style={{display: 'none'}} id={`show-less-${item.id}`} onClick={(e) => {
        e.stopPropagation();
        handleShowMoreLess(`show-more-${item?.id}`, `show-less-${item.id}`, 'less');
      }}>{videoTitle}</div>
    </>
    ) : (videoTitle || '--')
  );

  const youtubeCard = (
    <>
    <div onClick={handleOpenLink} className={styles.youtube_container}
      style={{color: theme.cardTextColor!, fontFamily: fontFamily, borderRadius: videoCardRadius, boxShadow: theme.boxShadow!}}>

      <div className={styles.iframe_wrapper}>
        <div className={styles.player_wrapper}>
          <img src={videoIcon} />
        </div>

        <div className={styles.youtubeDetails}>
          <div>
            <img src={item.videoThumbnail!} className={styles.youtubeVideoThumbnail}/>
          </div>
          {/* <span className={styles.youtubeVideoDuration}>
            {item.duration}</span> */}
        </div>
      </div>

      <div className={styles.description_container}>
        <p className={styles.video_title} style={{fontFamily: fontFamily, color: theme.nonCardTextColor!}}>
          {videoTitleContent}
        </p>
      </div>

    </div>
    {item?.metadata && contentFooter}
    </>
  );

  const ciqCard = (
    <>
    <div onClick={handleOpenLink} className={styles.ciq_container}
      style={{color: theme.cardTextColor!, fontFamily: fontFamily, borderRadius: videoCardRadius, boxShadow: theme.boxShadow!}}>

      <div className={styles.ciq_iframe_wrapper}>
        <div className={styles.ciq_player_wrapper}>
          {item?.platform == 'tiktok' ? <img src={playIcon} /> : <div className={styles.open_icon}><OpenInNewIcon /></div>}
        </div>

        <div className={styles.ciqDetails}>
          <div>
            <img src={item.videoThumbnail!} className={styles.ciqVideoThumbnail}/>
          </div>
        </div>
      </div>

      <div className={styles.ciq_description_container}>
        <p className={styles.ciq_video_title} style={{fontFamily: fontFamily, color: theme.nonCardTextColor!}}>
          {videoTitleContent}
        </p>
      </div>
    </div>
    {item?.metadata && contentFooter}
    </>
  );

  let cardContent;

  switch (activeTab) {
    case 'tiktok':
    case 'instagram': {
      cardContent = ciqCard;
      break;
    }
    case 'youtube': {
      cardContent = youtubeCard;
      break;
    }
  }

  return <div>
    {cardContent}
    </div>;
};

const PublicProfileFeaturedContentSection = ({
    mediakitUserId,
    sectionType,
    selectedStyle,
    items,
    theme,
    ownerUserId,
    nickname,
    email,
    setMonitizeID,
    setMonetizeObj,
  }: IPublicProfileFeaturedContentSection) => {
    const {t, i18n} = useTranslation();
    const [activeTab, setActiveTab] = useState(items[0]?.platform);
    const mappedItems = groupBy(items, "platform");
    let styles: any = '';
  
    if (selectedStyle === previewStyles.DESKTOP_PREVIEW) {
      styles = desktopPreviewStyle;
    } else if (selectedStyle === previewStyles.MOBILE_PREVIEW) {
      styles = mobilePreviewStyle;
    } else {
      styles = defaultStyle;
    }
    return (<div className={styles.content_container}>
        <div className={styles.tab_pill_container}>
            {Object.keys(mappedItems).map((tab) => (
                <div key={tab} className={`${styles.tab_item} ${activeTab === tab ? styles.tab_active : ''}`} onClick={() => setActiveTab(tab)}>{t(socialPlatform[tab])}</div>
            ))}
        </div>
        <div className={`${styles.content_wrapper} ${styles[activeTab]}`}>
            {
                mappedItems[activeTab]?.map((item: any, index: number) => {
                    return (
                      <div key={item.id}>
                        <PublicProfileFeaturedContentSectionItem
                          mediakitUserId={mediakitUserId}
                          sectionType={sectionType}
                          selectedStyle={selectedStyle}
                          theme={theme}
                          index={index}
                          item={item}
                          ownerUserId={ownerUserId}
                          nickname={nickname}
                          email={email}
                          setMonitizeID={setMonitizeID}
                          setMonetizeObj={setMonetizeObj}
                          activeTab={activeTab}
                        />
                      </div>
                    );
                  })
            }
        </div>
    </div>);
  };

export default PublicProfileFeaturedContentSection;
